// Plan 08-03 — Frontend mirror of Phase 4's three-tier content/loader.ts contract.
//
// Backend lives at apps/backend/src/content/loader.ts and runs:
//   tier 1: per-isolate Map<key, T>
//   tier 2: caches.default.match() (per-colo)
//   tier 3: bucket(env).get(r2Key) (R2 origin)
//
// In the browser we don't have caches.default or R2 bindings; the contract
// is downgraded to:
//   tier 1: module-scope Map<key, Promise<T>> — same in-flight de-duplication
//   tier 2: sessionStorage cache keyed by content-version pointer (best-effort)
//   tier 3: fetch() against the public path (or VITE_API_BASE_URL when set)
//
// Plan 04 wires the actual `themes:current` content-version pointer endpoint;
// Plan 08-03 ships the loader skeleton + a public-path fallback so theme
// alternates resolve in dev and CI even without R2.

const CACHE_KEY_PREFIX = 'runegambit/content' as const

const ALL_CACHES = new Set<Map<string, unknown>>()

export interface ContentLoaderConfig<T> {
  /** Surface key — e.g. 'themes', 'locales', 'balance'. Used in cache key. */
  cacheKeyPrefix: string
  /**
   * Build the public/HTTP path from the surface's input. The browser-side
   * loader maps this to a fetch() URL (relative when no API base configured).
   */
  buildPath: (subKey?: string) => string
  /** Decode raw bytes (e.g., JSON.parse on a UTF-8-decoded string). */
  decode: (bytes: ArrayBuffer) => T
}

/**
 * Build a content loader with a private tier-1 Map. Loader caches in-flight
 * promises by `subKey` so concurrent calls de-dupe to a single fetch.
 *
 * Returns the same descriptor reference on repeat calls (T1 hit).
 */
export function createContentLoader<T>(cfg: ContentLoaderConfig<T>) {
  const tier1 = new Map<string, Promise<T>>()
  ALL_CACHES.add(tier1 as Map<string, unknown>)

  return async function load(subKey?: string): Promise<T> {
    const cacheKey = subKey ?? '__root__'
    const cached = tier1.get(cacheKey)
    if (cached !== undefined) return cached

    const path = cfg.buildPath(subKey)
    const url = resolveUrl(path)

    const promise = (async () => {
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) {
        throw new Error(
          `CONTENT_FETCH_FAILED: ${cfg.cacheKeyPrefix}/${cacheKey} (HTTP ${res.status})`,
        )
      }
      const bytes = await res.arrayBuffer()
      return cfg.decode(bytes)
    })()

    // Cache the in-flight promise so concurrent callers de-dupe.
    tier1.set(cacheKey, promise)

    try {
      const decoded = await promise
      // Tier-2 best-effort: stash JSON in sessionStorage for next isolate
      // cold-start within the tab. Skipped in test envs that disable storage.
      try {
        if (typeof sessionStorage !== 'undefined') {
          const storageKey = `${CACHE_KEY_PREFIX}:${cfg.cacheKeyPrefix}:${cacheKey}`
          sessionStorage.setItem(storageKey, JSON.stringify(decoded))
        }
      } catch {
        // SessionStorage may be unavailable (private mode, quota, SSR). Skip.
      }
      return decoded
    } catch (err) {
      // On error, evict the failed promise so a retry doesn't latch onto it.
      tier1.delete(cacheKey)
      throw err
    }
  }
}

/**
 * Resolve a public path to a fetch URL.
 *
 * - Absolute URLs (https://...) pass through.
 * - Paths starting with '/' resolve against window.location.origin in the
 *   browser, or are returned as-is on the server.
 * - When VITE_API_BASE_URL is set, paths matching `/api/...` are rewritten
 *   to the API base (Plan 04 will route content through there once R2 is
 *   wired); other paths stay on the same origin (Vite serves /themes/* from
 *   public/).
 */
function resolveUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  const apiBase = (import.meta as { env?: Record<string, string> }).env
    ?.VITE_API_BASE_URL
  if (apiBase && path.startsWith('/api/')) {
    return `${apiBase.replace(/\/$/, '')}${path}`
  }
  return path
}

/** Test-only: clear every loader's tier-1 cache in one call. */
export function _clearContentCacheForTesting(): void {
  for (const m of ALL_CACHES) m.clear()
}
